import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Profile, ProfileStatus, UserRole } from '../types';
import { validateEmail } from '../utils/validation';

const allowedStatuses = ['APPROVED', 'ACTIVE'];

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: UserRole | null;
  status: ProfileStatus | null;
  loading: boolean;
  signIn: (identifier: string, password: string) => Promise<{ error?: Error; role?: UserRole | null; status?: ProfileStatus | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const isResetPath = location.pathname === '/reset-password';

  const fetchProfile = async (uid: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid);
    if (error) {
      setProfile(null);
      return null;
    }
    const profile = data && data[0] ? (data[0] as Profile) : null;
    if (profile && !allowedStatuses.includes(profile.status) && profile.status !== 'UNDER_REVIEW') {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setProfile(null);
      return null;
    }
    setProfile(profile);
    return profile;
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session?.user && !isResetPath) {
        fetchProfile(data.session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user && !isResetPath) {
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const signIn = async (identifier: string, password: string) => {
    if (!validateEmail(identifier)) {
      return { error: new Error('Enter a valid email') };
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email: identifier, password });
    if (error) return { error: new Error(error.message) };
    setSession(data.session);
    setUser(data.user);
    const fetched = data.user ? await fetchProfile(data.user.id) : null;

    if (fetched && !allowedStatuses.includes(fetched.status) && fetched.status !== 'UNDER_REVIEW') {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setProfile(null);
      return {
        error: new Error(
          fetched.status === 'BLOCKED'
            ? 'Your account is blocked. Contact support.'
            : 'Your account is not active. Contact support.'
        ),
      };
    }

    return { role: fetched?.role ?? null, status: fetched?.status ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        role: profile?.role ?? null,
        status: profile?.status ?? null,
        loading,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
